import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useEffect } from 'react';
import { Controller, useFieldArray, useFormContext, useFormState } from 'react-hook-form';
import { ControlledRadioGroup, ControlledTextField } from '../../../../shared/components';
import {
  BUILDING_TYPE_OPTIONS,
  GROUND_LEVEL_TYPE,
  GROUND_LEVEL_TYPE_OPTIONS,
  GROUND_LEVEL_USAGE,
  GROUND_LEVEL_USAGE_OPTIONS,
  WORK_AGE,
} from '../../../domain/work.constants';
import { completedUpto } from '../../../domain/typeOfWork.calculations';
import { BOOLEAN_OPTIONS } from '../constants';
import AlliedWorkSection from './AlliedWorkSection';
import FloorDiagram from './FloorDiagram';
import UpperFloorAssignmentRow from './UpperFloorAssignmentRow';

function get(obj, path) {
  return path.split('.').reduce((acc, k) => acc?.[k], obj);
}

export default function BuildingWorkSection({
  isAllied = false,
  drawings,
  lockedBuildingType,
}: {
  isAllied?: any;
  drawings: any;
  /** When set, Building Type is shown as this label rather than a radio list — see StepTypeOfWork. */
  lockedBuildingType?: string | null;
}) {
  const { watch, resetField, setValue, getValues } = useFormContext();
  const hasBasement = watch('hasBasement');
  const workAge = watch('workAge');

  useEffect(() => {
    if (hasBasement === true) {
      const current = getValues('basementCount');

      if (!current || Number(current) < 1) {
        setValue('basementCount', 1, {
          shouldDirty: false,
          shouldValidate: true,
        });
      }
    } else if (hasBasement === false) {
      resetField('basementCount');
    }
  }, [hasBasement, getValues, setValue, resetField]);

  return (
    <div className='column gap-3'>
      {lockedBuildingType === undefined ? (
        <BuildingTypeField />
      ) : (
        <LockedBuildingTypeField label={lockedBuildingType} />
      )}
      {/*
        Fields beside the drawing, with the drawing on a fixed narrower track so the
        inputs keep the room they need. Below `md` the two stack, since side by side the
        floor rows get too narrow to read their own labels.
      */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 312px' },
          alignItems: 'start',
          gap: 4,
        }}
      >
        <div className='column gap-3'>
          <BasementField drawings={drawings} hasBasement={hasBasement} />
          <GroundLevelFields drawings={drawings} hasBasement={hasBasement} />
          <UpperFloorSection />
          <AdditionalFloorSection workAge={workAge} />
          <StartingFloorSection workAge={workAge} />
          <StaircaseOhwtSection drawings={drawings} />
        </div>
        <FloorDiagram />
      </Box>
      {isAllied && <AlliedWorkSection label='8. Allied Work' orientation='column' />}
    </div>
  );
}

/**
 * Building Type as recorded on `work_jobs`, shown rather than chosen.
 *
 * The radio list below offers its own hard-coded options, which are not the rows in the
 * `building_types` master the job actually points at. Presenting both would invite a
 * choice this panel has no way to save, so when the panel is bound to a job the stored
 * label is all that is shown.
 */
function LockedBuildingTypeField({ label }: { label?: string | null }) {
  return (
    <div className='column gap-1'>
      <Typography variant='body1' color='text.primary' sx={{ whiteSpace: 'nowrap' }}>
        1. Building Type
      </Typography>
      <Typography variant='body1' color={label ? 'text.secondary' : 'text.disabled'}>
        {label || 'Not set on this job'}
      </Typography>
    </div>
  );
}

function BuildingTypeField() {
  const { control, watch, clearErrors } = useFormContext();
  const { errors } = useFormState({ name: 'buildingType' });
  const error = get(errors, 'buildingType');

  const buildingType = watch('buildingType');

  return (
    <div className='column gap-1'>
      <Typography variant='body1' color='text.primary' sx={{ whiteSpace: 'nowrap' }}>
        1. Building Type
      </Typography>
      <Controller
        name='buildingType'
        control={control}
        rules={{ required: 'Please select the type of building' }}
        render={({ field }) => (
          <div>
            <Box
              sx={{
                display: 'grid',
                gridAutoFlow: 'column',
                gridTemplateRows: 'repeat(10, auto)',
                columnGap: 4,
                rowGap: 1.5,
                alignItems: 'start',
              }}
            >
              {BUILDING_TYPE_OPTIONS.map((opt) => {
                const optionId = `buildingType-${opt.value}`;
                const isOtherBuilding = opt.value === 'OTHERS';
                return (
                  <div key={opt.value} className='row gap-8'>
                    <Stack
                      component='label'
                      htmlFor={optionId}
                      direction='row'
                      gap={1}
                      alignItems='center'
                      sx={{ cursor: 'pointer', alignSelf: 'start' }}
                    >
                      <Radio
                        id={optionId}
                        name='buildingType'
                        size='small'
                        disableRipple
                        checked={field.value === opt.value}
                        onChange={() => {
                          field.onChange(opt.value);
                          if (error) clearErrors('buildingType');
                        }}
                        sx={{
                          padding: 0,
                          color: 'grey.500',
                          '&.Mui-checked': { color: 'grey.700' },
                        }}
                      />
                      <Typography variant='body1' sx={{ whiteSpace: 'nowrap', color: 'grey.500' }}>
                        {opt.label}
                      </Typography>
                    </Stack>
                    {isOtherBuilding && (
                      <ControlledTextField
                        id='buildingTypeOther'
                        name='buildingTypeOther'
                        placeholder='Specify'
                        rules={{
                          required:
                            buildingType === 'OTHERS' ? 'Please specify the building type' : false,
                        }}
                        disabled={buildingType !== 'OTHERS'}
                        wrapperSx={{ width: 'auto' }}
                        showErrorInline
                      />
                    )}
                  </div>
                );
              })}
            </Box>
            <FormHelperText error>{error?.message}</FormHelperText>
          </div>
        )}
      />
    </div>
  );
}

function BasementField({ drawings, hasBasement }) {
  return (
    <div className='column'>
      <div className='row row--between'>
        <ControlledRadioGroup
          name='hasBasement'
          label='2. Basement'
          options={BOOLEAN_OPTIONS}
          wrapperOrientation='row'
          rules={{
            validate: (v) =>
              v === true || v === false || 'Please select if the building has a basement',
          }}
        />
        <DrawingCountRow
          label={hasBasement ? 'Basement' : 'Column-Footing'}
          count={drawings.basementDrawings}
        />
      </div>
      {hasBasement && (
        <ControlledTextField
          id='basementCount'
          name='basementCount'
          type='number'
          placeholder='Enter number'
          label='Total no. of Basement'
          rules={{
            required: 'Please enter number of basements',
            min: { value: 1, message: 'Please add at least 1 basement' },
          }}
          hideArrow
          orientation='row'
          wrapperSx={{ '& div': { justifyItems: 'flex-start' } }}
        />
      )}
    </div>
  );
}

function GroundLevelFields({ drawings, hasBasement }) {
  const { watch } = useFormContext();
  const groundLevelType = watch('groundLevelType');

  return (
    <div className='column'>
      <div className='row row--between'>
        <ControlledRadioGroup
          name='groundLevelType'
          label='3. Ground Level'
          rules={{ required: 'Please select ground level type' }}
          options={GROUND_LEVEL_TYPE_OPTIONS}
          wrapperOrientation='row'
        />
        <DrawingCountRow label='Ground' count={drawings.groundLevelDrawings} />
      </div>

      {groundLevelType === GROUND_LEVEL_TYPE.SINGLE && (
        <Stack gap={1} sx={{ ml: 4 }}>
          <GroundUsageGroup
            name='singleGroundUsage'
            mezzanineName='singleHasMezzanine'
            mezzanineMl={4}
          />
        </Stack>
      )}

      {groundLevelType === GROUND_LEVEL_TYPE.MULTIPLE && (
        <Stack gap={3} sx={{ ml: 4 }}>
          <div className='column gap-1'>
            <span>Lower Ground</span>

            {!hasBasement && (
              <ControlledRadioGroup
                name='lowerGroundRequiresColumnFooting'
                label='Column footing drawing required'
                options={BOOLEAN_OPTIONS}
                wrapperOrientation='row'
                rules={{
                  validate: (v) =>
                    v === true || v === false || 'Please select column footing option',
                }}
                wrapperSx={{ ml: 4 }}
              />
            )}

            <GroundUsageGroup
              name='lowerGroundUsage'
              sx={{ ml: 4 }}
              mezzanineName='lowerGroundHasMezzanine'
            />
          </div>

          <div className='column gap-1'>
            <span>Upper Ground</span>

            <GroundUsageGroup
              name='upperGroundUsage'
              sx={{ ml: 4 }}
              mezzanineName='upperGroundHasMezzanine'
            />
          </div>
        </Stack>
      )}
    </div>
  );
}

function GroundUsageGroup({
  name,
  label,
  sx = {},
  mezzanineName,
  showMezzanineFor = GROUND_LEVEL_USAGE.GROUND,
  mezzanineMl = 8,
}: { name: any; label?: any; sx?: any; mezzanineName: any; showMezzanineFor?: string; mezzanineMl?: number }) {
  const { watch, getValues, setValue } = useFormContext();
  const usageValue = watch(name);

  /**
   * A level that no longer takes a mezzanine (parking) drops the answer given while it did.
   * Hiding the question used to leave a stale "yes" behind, which the section drawing showed
   * and the save stored as a mezzanine over the parking.
   */
  useEffect(() => {
    if (!usageValue || usageValue === showMezzanineFor) return;
    if (getValues(mezzanineName) === false) return;
    setValue(mezzanineName, false, { shouldDirty: true, shouldValidate: false });
  }, [usageValue, showMezzanineFor, mezzanineName, getValues, setValue]);

  return (
    <>
      <ControlledRadioGroup
        id={name}
        name={name}
        label={label}
        rules={{ required: 'Please select the ground level usage' }}
        options={GROUND_LEVEL_USAGE_OPTIONS}
        orientation='column'
        wrapperSx={{ '& > div': { rowGap: 1.5 }, ...sx }}
      />

      {usageValue === showMezzanineFor && (
        <ControlledRadioGroup
          name={mezzanineName}
          label='Mezzanine'
          options={BOOLEAN_OPTIONS}
          wrapperOrientation='row'
          rules={{
            validate: (v) => v === true || v === false || 'Please select mezzanine option',
          }}
          wrapperSx={{ ml: mezzanineMl ?? 4 }}
        />
      )}
    </>
  );
}

function UpperFloorSection() {
  const { control, watch, setValue, trigger, formState } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name: 'upperFloorGroups' });

  const totalFloors = Number(watch('upperFloorsCount') || 0);
  /**
   * The rows' floors as they are now. `fields` is only a snapshot taken when a row is added
   * or removed, so it never sees floors picked afterwards: reading "floors taken by the
   * other rows" from it let a second row pick the first row's floors, which then failed the
   * check below and blocked the save.
   */
  const groups: any[] = watch('upperFloorGroups') || [];
  const assignmentError = get(formState.errors, '__upperFloorAssignmentCheck')?.message;

  useEffect(() => {
    if (!totalFloors) return;

    const groups = watch('upperFloorGroups') || [];

    groups.forEach((_, index) => {
      const floors = watch(`upperFloorGroups.${index}.floors`) || [];
      const filtered = floors.filter((f) => f <= totalFloors);

      if (filtered.length !== floors.length) {
        setValue(`upperFloorGroups.${index}.floors`, filtered, {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalFloors, setValue]);

  return (
    <div className='column gap-3'>
      <div className='row row--between'>
        <ControlledTextField
          id='upperFloorsCount'
          name='upperFloorsCount'
          label='4. Total no. of Upper Floors'
          type='number'
          placeholder='Enter number'
          rules={{
            required: 'Please enter number of upper floors (0 if none)',
            min: { value: 0, message: 'Upper floors cannot be negative' },
            validate: (v) => Number.isInteger(Number(v)) || 'Enter a whole number of floors',
            max: { value: 100, message: 'Maximum 100 floors' },
          }}
          hideArrow
          orientation='row'
          sx={{ width: 240 }}
        />
        {totalFloors > 0 && (
          <Button
            type='button'
            variant='outlined'
            sx={{
              borderRadius: '6px',
              border: '2px solid',
              borderColor: (theme) => alpha(theme.palette.primary.light, 0.4),
              color: (theme) => alpha(theme.palette.primary.light, 0.4),
              padding: '4px 8px',
              outline: 'none',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
            className='btn cta-outlined-btn'
            onClick={() => append({ name: '', floors: [] })}
          >
            + Add
          </Button>
        )}
      </div>

      {totalFloors > 0 && (
        <div className='column gap-1'>
          <Stack direction='row' gap={3} sx={{ pl: 2 }}>
            <Typography variant='body2' color='text.secondary' sx={{ width: 240 }}>
              Use of floor
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ width: 240 }}>
              Floor number
            </Typography>
          </Stack>
          {fields.map((field, index) => {
            const usedFloors = groups.flatMap((g, i) => (i === index ? [] : g?.floors || []));

            return (
              <UpperFloorAssignmentRow
                key={field.id}
                totalFloors={totalFloors}
                value={watch(`upperFloorGroups.${index}.floors`) || []}
                disabledFloors={usedFloors}
                index={index}
                onChange={(floors) => {
                  setValue(`upperFloorGroups.${index}.floors`, floors, {
                    shouldDirty: true,
                    shouldValidate: false,
                  });
                  // Once the check has failed, clear it as soon as the floors add up.
                  if (assignmentError) trigger('__upperFloorAssignmentCheck');
                }}
                canDelete={fields.length > 1}
                remove={(i) => {
                  remove(i);
                  if (assignmentError) trigger('__upperFloorAssignmentCheck');
                }}
              />
            );
          })}
          <Controller
            name='__upperFloorAssignmentCheck'
            control={control}
            rules={{
              validate: () => {
                const total = Number(watch('upperFloorsCount') || 0);
                if (!total) return true;

                const groups = watch('upperFloorGroups') || [];
                const allFloors = groups.flatMap((g) => g.floors || []);

                if (allFloors.length !== total) {
                  return 'All upper floors must be assigned';
                }

                if (new Set(allFloors).size !== total) {
                  return 'Each upper floor can only be assigned once';
                }

                return true;
              },
            }}
            // The message is the only sign of why Submit did nothing, so it is shown here.
            render={() =>
              assignmentError ? (
                <FormHelperText error sx={{ pl: 2 }}>{assignmentError}</FormHelperText>
              ) : null
            }
          />
        </div>
      )}
    </div>
  );
}

function AdditionalFloorSection({ workAge }) {
  const { watch } = useFormContext();
  const hasAdditionalFloors = watch('hasAdditionalFloors');

  if (workAge !== WORK_AGE.NEW) return null;

  return (
    <div className='row gap-8'>
      <ControlledRadioGroup
        name='hasAdditionalFloors'
        options={BOOLEAN_OPTIONS}
        wrapperOrientation='row'
        rules={{
          validate: (v) =>
            v === true || v === false || 'Please select if additional floors are provided',
        }}
        label='5. Provision for Additional Floors'
        wrapperSx={{ width: 'auto' }}
      />
      {hasAdditionalFloors && (
        <ControlledTextField
          id='additionalFloorsCount'
          name='additionalFloorsCount'
          type='number'
          placeholder='Enter number'
          rules={{
            required: 'Please enter number of additional floors',
            min: { value: 1, message: 'Please add at least 1 additional floor' },
          }}
          hideArrow
          orientation='row'
          label='No. of additional floors'
          wrapperSx={{ '& div': { justifyItems: 'flex-start' } }}
        />
      )}
    </div>
  );
}

/**
 * How far the building already stands, on modification/extension/addition jobs.
 *
 * Asked as "Work Completed Upto" — the highest floor already built — and stored as the floor
 * the new work starts at (this plus one), which is what the screen asked before. Floors 1 to
 * this one, the ground level and any basement are existing: the drawing beside this field
 * hatches them, and the drawing maths counts new slabs only from the floor above.
 */
function StartingFloorSection({ workAge }) {
  const { watch } = useFormContext();
  const completed = completedUpto({ workCompletedUpto: watch('workCompletedUpto') });

  if (workAge !== WORK_AGE.EXISTING) return null;

  return (
    <div className='row gap-8'>
      <ControlledTextField
        id='workCompletedUpto'
        name='workCompletedUpto'
        type='number'
        placeholder='Enter floor number'
        rules={{
          required: 'Please enter the floor the existing work is completed up to',
          min: { value: 0, message: 'Enter 0 when only the ground level is built' },
        }}
        hideArrow
        orientation='row'
        label='5. Work Completed Upto'
        wrapperSx={{ '& div': { justifyItems: 'flex-start' } }}
      />
      {completed != null && (
        <Typography variant='body2' color='text.secondary' sx={{ whiteSpace: 'nowrap' }}>
          {completed === 0
            ? 'Ground level built; new work from floor 1'
            : `Built up to floor ${completed}; new work from floor ${completed + 1}`}
        </Typography>
      )}
    </div>
  );
}

/**
 * Staircase cap, overhead water tank, lift machine room.
 *
 * The slab maths used to add a flat two — "roof + OHT" — to every job, which charged for a
 * tank nobody had asked about and charged once for a job that has all three. The roof keeps
 * its own slab and these are counted here instead, so the structural drawings follow what
 * is actually on the roof.
 */
function StaircaseOhwtSection({ drawings }) {
  const { watch, resetField, setValue, getValues } = useFormContext();
  const hasStaircaseOhwt = watch('hasStaircaseOhwt');

  // Answering "yes" means at least one of them, so the count starts there rather than at a
  // blank box that would silently contribute nothing.
  useEffect(() => {
    if (hasStaircaseOhwt === true) {
      const current = getValues('staircaseOhwtCount');
      if (!current || Number(current) < 1) {
        setValue('staircaseOhwtCount', 1, { shouldDirty: false, shouldValidate: true });
      }
    } else if (hasStaircaseOhwt === false) {
      resetField('staircaseOhwtCount');
    }
  }, [hasStaircaseOhwt, getValues, setValue, resetField]);

  return (
    <div className='column'>
      <div className='row row--between'>
        <ControlledRadioGroup
          name='hasStaircaseOhwt'
          label='6. Staircap or OHWT/LMR'
          options={BOOLEAN_OPTIONS}
          wrapperOrientation='row'
          rules={{
            validate: (v) =>
              v === true || v === false || 'Please select if the building has a staircase cap, OHWT or LMR',
          }}
          wrapperSx={{ width: 'auto' }}
        />
        <DrawingCountRow label='Staircap / OHWT / LMR' count={drawings.staircaseOhwtSlabs} />
      </div>
      {hasStaircaseOhwt && (
        <ControlledTextField
          id='staircaseOhwtCount'
          name='staircaseOhwtCount'
          type='number'
          placeholder='Enter number'
          label='No. of Staircap / OHWT / LMR'
          rules={{
            required: 'Please enter how many there are',
            min: { value: 1, message: 'Please add at least 1' },
          }}
          hideArrow
          orientation='row'
          wrapperSx={{ '& div': { justifyItems: 'flex-start' } }}
        />
      )}
    </div>
  );
}

/**
 * Built-up area for the whole job, in sq. ft.
 *
 * It describes the building, so it is collected here and saved on the job. The QUOTATION
 * screen's Area box reads this back instead of asking for it again — one area per job,
 * rather than one per quotation raised against it.
 */
export function TotalAreaField({ numbered = true }: { numbered?: boolean } = {}) {
  return (
    <ControlledTextField
      id='totalAreaSqFt'
      name='totalAreaSqFt'
      inputMode='numeric'
      placeholder='Enter total area'
      label={numbered ? '7. Total Area' : 'Total Area'}
      suffix='sq.ft.'
      hideArrow
      rules={{
        required: 'Please enter the total area (0 if not known yet)',
        validate: (v) => (v !== '' && Number.isFinite(Number(v)) && Number(v) >= 0) || 'Total area cannot be negative',
      }}
      showErrorInline
      orientation='row'
      sx={{ maxWidth: 260, width: '100%' }}
      wrapperSx={{ '& div': { justifyItems: 'flex-start' } }}
    />
  );
}

function DrawingCountRow({ label, count }) {
  return (
    <div className='row gap-3'>
      <Typography variant='body1' sx={{ whiteSpace: 'nowrap' }}>
        {label} Drawings
      </Typography>
      <span className='text--secondary'>{count}</span>
    </div>
  );
}
